(ns nosco.router
  (:require
   ["react" :as React]
   [clojure.edn :as edn]
   [clojure.string :as string]
   [goog.object :as gobj]
   [nosco.sentry :as sentry]
   [nosco.tracking.track :as track]
   [reitit.frontend]
   [reitit.frontend.controllers]
   [reitit.frontend.easy]
   [reitit.frontend.history :as rfh]
   [reitit.impl :as rimpl]))

(defonce current-route-match (atom nil))

(defn current-route-name []
  (-> @current-route-match :data :name))

(defn current-route-path-params []
  (-> @current-route-match :path-params))

(defn current-route-query-params []
  (-> @current-route-match :query-params))

(defn match->uri-string [match]
  (let [{:keys [query fragment]}  (:parameters match)
        path (:path match)
        url (str path
                 (when (seq query)
                   (str "?" (rimpl/query-string query)))
                 (when fragment (str "#" fragment)))]
    url))

(defn current-full-url []
  (let [match @current-route-match]
    (match->uri-string match)))

(def RouteContext (React/createContext nil))

(defn- location [] (.-href js/window.location))

(defn- router [routes]
  (reitit.frontend/router routes {:conflicts nil}))

(defn- remove-trailing-slash [url]
  (let [url (js/URL. url js/location)
        path (.-pathname url)]
    (when (string/ends-with? path "/")
      (let [non-slash-path (subs path 0 (dec (count path)))]
        (set! (.-pathname url) non-slash-path)
        (str url)))))

(defn- match-by-path-without-slash! [router url]
  (when-let [url' (remove-trailing-slash url)]
    (when-let [match (reitit.frontend/match-by-path router url')]
      (.replaceState js/history nil "" url')
      match)))

(defn- track-page-view! [match]
  (when-let [tracking-name (get-in match [:data :tracking-name])]
    (track/page-view tracking-name)))

(defn- track-navigation [curr-match new-match]
  (let [{:keys [path data]} new-match]
    (sentry/add-breadcrumb #js {:path path
                                :parameters (clj->js (:parameters data))
                                :name (str (:name data))}
                           {:category :router}))
  (sentry/add-navigation (:path curr-match) (:path new-match)))

(defn- apply-controllers [old-match new-match]
  (reitit.frontend.controllers/apply-controllers (:controllers old-match) new-match))

(defonce ^:private transient-url-stack (atom []))

(defn- transient-url-active? []
  (seq @transient-url-stack))

(defn initiate-transient-url-context! [context pop-state-handler state]
  (swap! transient-url-stack conj
         {::context context
          ::pop-state-handler pop-state-handler
          ::state state
          ::previous-location (str (.-location js/window))})
  (.replaceState js/history
                 (pr-str {::context context
                          ::state state
                          ::previous-location
                          (str (.-location js/window))})
                 ""
                 (str (.-location js/window))))

(defn push-transient-url!
  "Push a new URL to the history stack without changing the current page.
   This is useful for transient UI states that should be bookmarkable."
  ([context url]
   (push-transient-url! context url nil))
  ([context url state]
   (js/console.log "PUSHING TRANSIENT"  {::context context
                                         ::state state})
   (.pushState js/history (pr-str {::context context
                                   ::state state}) "" url)))

(defn unwind-transient-url-stack!
  "Pop the last transient URL from the history stack."
  ([context']
   (unwind-transient-url-stack! context' true))
  ([context' mutate-history?]
   (js/console.log "UNWINDING TRANSIENT" context')
   (let [stack @transient-url-stack
         {::keys [context previous-location]} (peek stack)]
     (if (= context context')
       (do
         (swap! transient-url-stack pop)
         (when mutate-history?
           (.pushState js/history nil "" previous-location)))

       (js/console.log "Invalid unwinding of transient URL" {:stack stack
                                                             :context context
                                                             :context' context'})))))

(defn- handle-router-navigate [new-match {:keys [router]}]
  ;; new-match will be nil when the router is initialized on a page with a path
  ;; which doesn't match a route -- in this case we check for a trailing slash
  (when-let [new-match (or new-match (match-by-path-without-slash! router (location)))]
    (track-navigation @current-route-match new-match)
    (track-page-view! new-match)
    ;; do not change the app state at all, when a transient URL is shown
    (if (transient-url-active?)
      (do
        (js/console.log "transient url active, ignoring navigation match"))
      (swap! current-route-match
             (fn [old-match]
               (assoc new-match :controllers
                      (apply-controllers old-match new-match)))))))

(defonce ^:private last-popstate (atom nil))
(defn listen-to-popstate! []
  (.addEventListener js/window "popstate"
                     (fn [e]
                       (reset! last-popstate (.-state e))
                       (when (transient-url-active?)
                         (let [stack @transient-url-stack
                               {::keys [pop-state-handler]} (peek stack)
                               event-state (try (edn/read-string (.-state e))
                                                (catch :default _
                                                  nil))]
                           (js/console.log "TRANSIENT POPSTATE" (.-state e) stack)
                           (when (some? pop-state-handler)
                             (pop-state-handler (::state event-state))))))))

(defonce
  ^{:doc "A counter for the number of route matches that have occurred. Used to check whether the
previous page will lead outside of the platform."}
  ;; TODO Replace this with putting the previous URL into the history state so that we
  ;; can always navigate backwards within the Nosco platform, even across page reloads.
  ;; This probably requires replacing reitit-frontend with something more flexible or custom.
  navigation-counter
  (atom 0))

(def custom-url-handlers (atom []))

(defn add-custom-url-handler! [handler]
  (swap! custom-url-handlers conj handler))

(defn remove-custom-url-handler! [handler]
  (swap! custom-url-handlers #(remove #{handler} %)))

(defn- run-custom-url-handlers [router e el ^js uri]
  (let [handle (atom false)]
    (doseq [handler @custom-url-handlers
            :let [match (reitit.frontend/match-by-path router (.getPath uri))]
            :when match]
      (let [handle? (handler match e el uri)]
        (when handle? (reset! handle true))))
    @handle))

(defn start!
  ([routes]
   (start! routes handle-router-navigate))
  ([routes on-navigate]
   (start! routes on-navigate {:use-fragment false
                               :ignore-anchor-click?
                               (fn [router e el uri]
                                 (let [reitit-handles?      (rfh/ignore-anchor-click? router e el uri)
                                       transient-push?      (gobj/get (.-dataset el) "transientPush")
                                       reitit-ignore-click? (gobj/get (.-dataset el) "reititignoreclick")
                                       custom-handled?      (and reitit-handles?
                                                                 (not reitit-ignore-click?)
                                                                 (run-custom-url-handlers router e el uri))]
                                   (when (and reitit-handles?
                                              (not custom-handled?)
                                              (transient-url-active?)
                                              (not transient-push?))
                                     (reset! transient-url-stack []))
                                   (and reitit-handles?
                                        (not reitit-ignore-click?)
                                        (not custom-handled?))))}))
  ([routes on-navigate opts]
   (listen-to-popstate!)
   (reitit.frontend.easy/start!
    (router routes)
    (fn [match history]
      (when (not= (:path match) (:path @current-route-match))
        (swap! navigation-counter inc))
      (on-navigate match history))
    opts)))

(defn reload! [routes]
  (swap! reitit.frontend.easy/history assoc :router (router routes)))

(defn href
  ([k] (href k nil nil))
  ([k params] (href k params nil))
  ([k params query] (reitit.frontend.easy/href k params query)))

(defn push-state
  ([k] (push-state k nil nil))
  ([k params] (push-state k params nil))
  ([k params query] (reitit.frontend.easy/push-state k params query)))

(defn replace-state
  ([k] (replace-state k nil nil))
  ([k params] (replace-state k params nil))
  ([k params query] (reitit.frontend.easy/replace-state k params query)))

(defn push-state-string [base-href query-params anchor]
  (let [href (str base-href
                  (when (seq query-params)
                    (str "?" (rimpl/query-string query-params)))
                  (when anchor (str "#" anchor)))]
    (.pushState js/window.history nil "" href)
    (reitit.frontend.history/-on-navigate @reitit.frontend.easy/history href)))

(defn nav-go-home []
  (push-state :nosco.routes.user/root))

(defn nav-go-back
  "Navigates to the previous item on the history stack, without leaving the platform.
  If it's not possible to navigate backwards, fallback to default-href, or the home page."
  ([]
   (nav-go-back nil))
  ([default-href]
   (if (>= 1 @navigation-counter)
     (if default-href
       (push-state-string default-href nil nil)
       (nav-go-home))
     (.back js/history))))

(defn- get-current-url []
  (js/URL. (.. js/window -location -href)))

(defn user-level-route? [route]
  (if route
    (let [route-name (get-in route [:data :name])
          prefix (namespace route-name)]
      (= "nosco.routes.user" prefix))
    ;; for unknown routes we rely on the admin prefix
    (let [url (get-current-url)
          path (.-pathname url)]
      (not (string/starts-with? path "/admin")))))

;; TODO the following functions need cleanup, they are confusing
(defn update-query-params
  "Updates the current query parameter under `key`.
  Pass `nil` to remove the key entirely"
  [key s]
  (let [key (name key)
        url (get-current-url)
        params (.-searchParams url)]
    (if s
      (.set params key s)
      (.delete params key))
    params))

(defn update-query-params-loudly! [key s]
  (let [url (get-current-url)
        params (update-query-params key s)]
    (set! (.-search url)
          (let [params-s (.toString params)]
            (if-not (= params-s "")
              (str "?" (.toString params))
              "")))
    (.pushState js/history nil "" url)
    (reitit.frontend.history/-on-navigate @reitit.frontend.easy/history (str url))))

(defn update-query-params-silently!
  "Updates the current query parameter under `key` without triggering a navigation event.
  Pass `nil` to remove the key entirely"
  [key s]
  (let [url (get-current-url)
        params (update-query-params key s)]
    (set! (.-search url)
          (let [params-s (.toString params)]
            (if-not (= params-s "")
              (str "?" (.toString params))
              "")))
    (.replaceState js/history nil "" url)))

(defn update-query-params2!
  [m & {:keys [add-history navigate state]}]
  (let [url (get-current-url)
        params (js/URLSearchParams. (.-search url))]
    (doseq [[k s] m]
      (if (nil? s)
        (.delete params (name k))
        (.set params (name k) s)))

    (set! (.-search url)
          (.toString params))

    (if add-history
      (.pushState js/history state "" (str url))
      (.replaceState js/history state "" (str url)))
    (when navigate
      (reitit.frontend.history/-on-navigate @reitit.frontend.easy/history (str url)))))

(defn save-history-state!
  "Save an opaque state object to the current entry of the browser history.
   Strings work best."
  [state]
  (let [url (js/URL. (.. js/window -location -href))]
    (.replaceState js/history state "" url)))

(defn read-history-state!
  "Read the last saved state from the browser history. Be careful when decoding,
   it might be unexpected."
  []
  @last-popstate)
